"use client";

import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { TypesenseSearchModal } from "./TypesenseSearchModal";
import {
  // CosmosButton,
  CosmosIconSearch,
  // CosmosInput,
  CosmosText,
} from "@cosmos/web-react";
import classnames from "classnames/bind";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./TypesenseSearchField.module.css";

const cx = classnames.bind(styles);

export type TypesenseSearchFieldProps = {
  localeString: string;
  searchInputLabel?: string;
  searchInputPlaceholderLabel?: string;
  // TODO: Re-enable clear label when search is active. Temporarily
  // searchInputClearLabel?: string;
  searchSeeAllResultsButtonLabel?: string;
  searchNoResultsLabel?: string;
  searchModalCloseLabel?: string;
  // TODO: Re-enable close label when search is active. Temporarily
  // closeButtonLabel?: string;
};

export function TypesenseSearchField({
  localeString,
  searchInputLabel = "Search questions",
  searchInputPlaceholderLabel = "Search all FAQs",
  // searchInputClearLabel = "Clear search",
  searchSeeAllResultsButtonLabel = "See all results",
  searchNoResultsLabel = "No matching questions found",
  searchModalCloseLabel = "Close search",
  // closeButtonLabel = "Close",
}: TypesenseSearchFieldProps) {
  const { selectedSearchQuery } = useContactPageRouterContext();
  const [isModalOpen, setModalOpen] = useState(false);

  // Live mirror of the modal's autocomplete input so the closed-state trigger
  // updates as the user types and resets when they clear the input. Seeded
  // from the URL-driven query and kept in sync if the URL changes externally.
  const [draftQuery, setDraftQuery] = useState(selectedSearchQuery ?? "");
  useEffect(() => {
    setDraftQuery(selectedSearchQuery ?? "");
  }, [selectedSearchQuery]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  // While the modal is open and the user is typing, mirror the live input.
  // With a blank modal input (just opened, or after cancel), fall back to the
  // URL-driven term so the trigger keeps showing the active search. While a
  // submit is in flight, prefer the draft over a stale URL query.
  const urlQuery = selectedSearchQuery ?? "";
  const triggerLabel =
    isModalOpen && draftQuery.trim()
      ? draftQuery
      : draftQuery.trim() && draftQuery !== urlQuery
        ? draftQuery
        : urlQuery || draftQuery || searchInputPlaceholderLabel;

  return (
    <div className={cx("search-wrapper")}>
      <div className={cx("search-form")}>
        <div className={cx("input-wrapper")}>
          {/* Visual-only stand-in for the search input. Wrapped in `inert` so
              nothing inside (including the shadow-DOM input) is focusable or
              clickable — the `.trigger-overlay` button on top handles all
              clicks and keyboard activation. */}
          {/* <div className={cx("visual-input")} inert>
            <CosmosInput
              className={cx("autocomplete-input")}
              autocomplete="off"
              hideLabel
              readonly
              label={searchInputLabel}
              iconPlacement="before"
              placeholder={searchInputPlaceholderLabel}
              value={selectedSearchQuery ?? ""}
              clear-aria-label={searchInputClearLabel}
            >
              <div className={cx("icon-placeholder")} slot="icon" />
            </CosmosInput>
            <CosmosButton
              type="button"
              className={cx("submit-icon-button")}
              buttonAriaLabel={searchInputLabel}
              kind="primary"
              shape="rectangle"
            >
              <CosmosIconSearch />
            </CosmosButton>
          </div> */}
          <button
            type="button"
            className={cx("trigger-overlay")}
            aria-label={searchInputLabel}
            aria-haspopup="dialog"
            aria-expanded={isModalOpen}
            onClick={openModal}
          >
            <CosmosIconSearch className={cx("icon-placeholder")} />
            <CosmosText
              kind="subtle"
              size="small"
              tag="span"
              aria-hidden={true}
              clamp
              className={cx("trigger-overlay-ellipsis")}
            >
              {triggerLabel}
            </CosmosText>
          </button>
        </div>
      </div>

      <TypesenseSearchModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onQueryChange={setDraftQuery}
        localeString={localeString}
        searchInputLabel={searchInputLabel}
        searchInputPlaceholderLabel={searchInputPlaceholderLabel}
        // TODO: Re-enable clear label when search is active. Temporarily
        // searchInputClearLabel={searchInputClearLabel}
        searchSeeAllResultsButtonLabel={searchSeeAllResultsButtonLabel}
        searchNoResultsLabel={searchNoResultsLabel}
        searchModalCloseLabel={searchModalCloseLabel}
        // TODO: Re-enable close label when search is active. Temporarily
        // closeButtonLabel={closeButtonLabel}
      />
    </div>
  );
}
